import { useState } from 'react';

import { Button, Dropdown, FieldLabel, InputSwitch, NumericInput } from 'fluxo-ui';
import { PlayIcon } from 'fluxo-ui/icons';

import { LiveQuizSettings } from '../../../types/index.ts';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface LiveQuizSetupProps {
    examTitle: string;
    questionCount: number;
    starting: boolean;
    onStart: (settings: LiveQuizSettings) => void;
}

const defaultSettings: LiveQuizSettings = {
    questionTimeLimit: 30,
    pointsPerCorrectAnswer: 1000,
    speedBonusEnabled: true,
    maxSpeedBonus: 500,
    showLeaderboardAfterEachQuestion: true,
    randomizeQuestionOrder: false,
    randomizeOptionOrder: true,
    streakBonusEnabled: true,
    maxStreakBonus: 500,
    autoAdvance: true,
};

const timeLimitOptions: ListItem[] = [
    { label: '10 seconds', value: '10' },
    { label: '15 seconds', value: '15' },
    { label: '20 seconds', value: '20' },
    { label: '30 seconds', value: '30' },
    { label: '45 seconds', value: '45' },
    { label: '60 seconds', value: '60' },
    { label: '90 seconds', value: '90' },
];

const LiveQuizSetup: React.FC<LiveQuizSetupProps> = ({ examTitle, questionCount, starting, onStart }) => {
    const [settings, setSettings] = useState<LiveQuizSettings>(defaultSettings);

    const updateSetting = <K extends keyof LiveQuizSettings>(key: K, value: LiveQuizSettings[K]) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
    };

    return (
        <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-6">
            <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white">{examTitle}</h2>
                <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                    {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FieldLabel label="Time per question" required>
                    <Dropdown
                        options={timeLimitOptions}
                        value={String(settings.questionTimeLimit)}
                        onChange={(e: ComponentEvent<string>) => updateSetting('questionTimeLimit', parseInt(e.value, 10))}
                    />
                </FieldLabel>

                <FieldLabel label="Points per correct answer">
                    <NumericInput
                        value={settings.pointsPerCorrectAnswer}
                        onChange={(e: ComponentEvent<number | undefined>) =>
                            updateSetting('pointsPerCorrectAnswer', e.value ?? defaultSettings.pointsPerCorrectAnswer)
                        }
                        min={100}
                        max={5000}
                        step={100}
                    />
                </FieldLabel>
            </div>

            <div className="space-y-3 pt-5 border-t border-slate-200 dark:border-slate-800">
                <FieldLabel label="Speed bonus" hint="Faster correct answers score more">
                    <InputSwitch
                        checked={settings.speedBonusEnabled}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('speedBonusEnabled', e.value)}
                    />
                </FieldLabel>

                {settings.speedBonusEnabled && (
                    <div className="pl-4 border-l-2 border-slate-200 dark:border-slate-800">
                        <FieldLabel label="Maximum speed bonus">
                            <NumericInput
                                value={settings.maxSpeedBonus}
                                onChange={(e: ComponentEvent<number | undefined>) =>
                                    updateSetting('maxSpeedBonus', e.value ?? defaultSettings.maxSpeedBonus)
                                }
                                min={0}
                                max={2000}
                                step={50}
                            />
                        </FieldLabel>
                    </div>
                )}

                <FieldLabel label="Streak bonus" hint="Reward consecutive correct answers">
                    <InputSwitch
                        checked={settings.streakBonusEnabled}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('streakBonusEnabled', e.value)}
                    />
                </FieldLabel>

                {settings.streakBonusEnabled && (
                    <div className="pl-4 border-l-2 border-slate-200 dark:border-slate-800">
                        <FieldLabel label="Maximum streak bonus">
                            <NumericInput
                                value={settings.maxStreakBonus}
                                onChange={(e: ComponentEvent<number | undefined>) =>
                                    updateSetting('maxStreakBonus', e.value ?? defaultSettings.maxStreakBonus)
                                }
                                min={0}
                                max={2000}
                                step={50}
                            />
                        </FieldLabel>
                    </div>
                )}
            </div>

            <div className="space-y-3 pt-5 border-t border-slate-200 dark:border-slate-800">
                <FieldLabel label="Advance automatically" hint="Move through reveal and leaderboard without host clicks">
                    <InputSwitch
                        checked={settings.autoAdvance}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('autoAdvance', e.value)}
                    />
                </FieldLabel>

                <FieldLabel label="Leaderboard between questions">
                    <InputSwitch
                        checked={settings.showLeaderboardAfterEachQuestion}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('showLeaderboardAfterEachQuestion', e.value)}
                    />
                </FieldLabel>

                <FieldLabel label="Shuffle question order">
                    <InputSwitch
                        checked={settings.randomizeQuestionOrder}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('randomizeQuestionOrder', e.value)}
                    />
                </FieldLabel>

                <FieldLabel label="Shuffle answer options">
                    <InputSwitch
                        checked={settings.randomizeOptionOrder}
                        onChange={(e: ComponentEvent<boolean>) => updateSetting('randomizeOptionOrder', e.value)}
                    />
                </FieldLabel>
            </div>

            <Button
                label="Create session"
                leftIcon={PlayIcon}
                variant="primary"
                size="lg"
                className="w-full"
                isLoading={starting}
                disabled={starting || questionCount === 0}
                onClick={() => onStart(settings)}
            />
        </section>
    );
};

export default LiveQuizSetup;
